import React from 'react';
import _ from 'lodash';
import {
  diffItem,
  createChangeObj,
  calculatePeriod ,
  calculateActualCostTotal
} from 'common/requestDetails/utils';
import {produce} from 'immer';
import SimpleShared from 'go/components/modules/requestDetails/v3/items/shared/SimpleShared';
import {
  CHANGES_TYPES,
  VALUE_CHANGE_DISPLAY,
  CARD_TYPES,
  ADDITIONAL_INFORMATION,
  COST,
  ACTUAL_TOTAL,
  SCOPE_OF_WORK_TOTAL,
  ALLOWED_EXCESS_TOTAL,
  DISALLOWED_TOTAL,
  CATEGORIZED_SUM,
  ACTUAL_ALLOWED_TOTAL,
  NOT_FULLY_IN_SCOPE
} from 'common/requestDetails/constants';


export default class Simple extends React.Component {
  constructor(props) {
    super(props);
    const notFullyInScope =_.get(props, "item."+ADDITIONAL_INFORMATION+"."+NOT_FULLY_IN_SCOPE, false)
    this.state = {
      lineItem: this.props.item,
      editMode: notFullyInScope,
    };
  }

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (lineItem) => {
    return new Promise((resolve) => {
      this.setState({lineItem}, () => resolve());
    });
  };

  resetCostValues = (cost, actualTotal) => {
    _.set(cost, ACTUAL_ALLOWED_TOTAL, actualTotal);
    _.set(cost, CATEGORIZED_SUM, actualTotal);
    _.set(cost, SCOPE_OF_WORK_TOTAL, actualTotal);
    _.set(cost, ALLOWED_EXCESS_TOTAL, 0);
    _.set(cost, DISALLOWED_TOTAL, 0);
  };

  resetBudgetCategorization = (item, notFullyInScope = false) => {
    let costs = _.get(item, COST, []);
    this.toggleEditMode();
    if(!notFullyInScope) {
      return produce(costs, (draft) => {
        if (draft !== undefined) {
          _.forEach(draft, (cost, index) => {
            _.forEach(cost, (newCost, ind) => {
              let itemActualTotal = calculateActualCostTotal(newCost);
              this.resetCostValues(newCost, itemActualTotal);
            });
          });
        }
        return draft;
      });
    } else {
      return produce(costs, (draft) => {
        if (draft !== undefined) {
          _.forEach(draft, (cost, index) => {
            _.forEach(cost, (newCost, ind) => {

              let itemActualTotal = _.get(newCost, ACTUAL_TOTAL, 0);
              let itemActualAllowedTotal = _.get(newCost, ACTUAL_ALLOWED_TOTAL, 0);
              let categorizedSum = _.get(newCost, CATEGORIZED_SUM);
              let scopeOfWorkTotal = _.get(newCost, SCOPE_OF_WORK_TOTAL);
              let allowedExcessTotal = _.get(newCost, ALLOWED_EXCESS_TOTAL);
              let disallowedTotal = _.get(newCost, DISALLOWED_TOTAL);

              if ( 
                categorizedSum===undefined || 
                scopeOfWorkTotal===undefined || 
                allowedExcessTotal===undefined ||
                disallowedTotal===undefined || 
                itemActualAllowedTotal ===undefined
              ) {
                this.resetCostValues(newCost, itemActualTotal);
              } 
            });
          });
        }
        return draft;
      });
    }
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1, extraArgs) => {
    let newLineItem = _.cloneDeep(this.props.item);
    _.set(newLineItem, name, value);
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    if(_.get(extraArgs, "resetFields")){
      _.each(extraArgs.resetFields, field => {
        _.unset(newLineItem, field)
      })
  }
    return this.handleChange(newLineItem);
  };

  handleNotFullyInScopeItemChange = (name, value, ind = -1) => {
    let oldItem = this.props.originalItem;
    let newItem = this.props.item;
    
    let newLineItem = oldItem ? _.cloneDeep(oldItem) : _.cloneDeep(newItem);
    _.set(newLineItem, name, value);

    let updatedCosts = this.resetBudgetCategorization(newLineItem, value);
    _.set(newLineItem, COST, updatedCosts);
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleNotFullyInScopeItemChangeAndBlur = (name, value, fieldName) => {
    this.handleNotFullyInScopeItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  }

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
  };

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig} = this.props;
    const period = calculatePeriod(itemConfig);

    let displayCostRow = _.get(oldItem, ['cost', 0], []).length > 1;
    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions
      );
    });
    
    let changes = [];

    if (oldItem) {
      const findCost = (item, year, ind) => {
        return _.get(item, ['cost', year, ind], {});
      };

      _.forEach(_.get(newItem, 'cost', []), (phaseCost, phaseInd) => {
        _.forEach(phaseCost, (newCost, ind) => {
          const oldCost = findCost(oldItem, phaseInd, ind);
          const total = _.get(newCost, ACTUAL_ALLOWED_TOTAL);
          const totalOld = calculateActualCostTotal(oldCost);
          let costRow = displayCostRow ? _.get(newCost, 'costRow', 0) : 0;
          if (total && _.toNumber(total) !== _.toNumber(totalOld)) {
            let message = `Total costs ${ind + 1} ${_.upperFirst(period.type)} ${
              phaseInd + 1
            } ${ACTUAL_TOTAL}`;

            changes.push({
              ...createChangeObj(
                CHANGES_TYPES.VALUE_CHANGE,
                ACTUAL_TOTAL,
                totalOld,
                total,
                period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[ACTUAL_TOTAL]
              ),
              costRow: costRow,
              periodType: period.type,
            });
          }
        });
      });
    }

    let updatedItem = produce(newItem, (draft) =>
      _.set(
        draft,
        [ADDITIONAL_INFORMATION, 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      )
    );

    return updatedItem;
  };

  renderChangesSection = () => {
    return (
      <React.Fragment />
    );
  };

  render() {
 
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        costType={CARD_TYPES.SIMPLE}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleNotFullyInScopeItemChange={this.handleNotFullyInScopeItemChange}
        handleNotFullyInScopeItemChangeAndBlur={this.handleNotFullyInScopeItemChangeAndBlur}
        {...this.props}
      />
    );
  }
}
